/**
 * Resolve API base URL from environment variable or default to current origin
 * 
 * Supports multiple formats:
 * - Absolute: http://api.example.com or https://api.example.com
 * - Protocol-relative: //api.example.com
 * - Root-relative: /api or /backend/api
 * - Relative: api or backend/api
 * - Default: window.location.origin (current domain)
 */
export function resolveApiBaseUrl(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL ?? '').trim()

  // If explicitly provided
  if (raw.length > 0) {
    // Absolute http(s) URL
    if (/^https?:\/\//i.test(raw)) {
      return trimTrailingSlash(raw)
    }

    // Protocol-relative, e.g. //api.example.com
    if (raw.startsWith('//')) {
      return trimTrailingSlash(`${window.location.protocol}${raw}`)
    }

    // Root-relative path, e.g. /api or /backend/api
    if (raw.startsWith('/')) {
      return trimTrailingSlash(`${window.location.origin}${raw}`)
    }

    try {
      // Try to resolve against current origin (supports relative like "api" or "backend/api")
      const url = new URL(raw, window.location.origin)
      return trimTrailingSlash(`${url.origin}${url.pathname}`)
    } catch {
      // Fall through to default
    }
  }

  // Default: current origin (same domain as frontend)
  return window.location.origin
}

/**
 * Resolve auth base URL (adds /api/auth to base URL)
 */
export function resolveAuthBaseUrl(): string {
  return `${resolveApiBaseUrl()}/api/auth`
}

/**
 * Helper to remove trailing slashes
 */
function trimTrailingSlash(input: string): string {
  return input.replace(/\/+$/, '')
}
